/**
 * Item Impact Events — Cost of Delay retrospective tracking
 *
 * Distinct from the predictive `cost_of_delay` attribute (a forward-looking score).
 * These are recorded *after the fact* to document what actually happened because an
 * item was deferred or deprioritized. The data calibrates future scoring and makes
 * prioritization decisions defensible over time.
 */

// ── Event type enum ───────────────────────────────────────────────────────────

export type ImpactEventType =
  | 'revenue_missed'
  | 'incident'
  | 'churn'
  | 'contract_risk'
  | 'compliance_breach'
  | 'reputation'
  | 'other';

// ── Display metadata ──────────────────────────────────────────────────────────

export interface ImpactEventTypeMeta {
  label: string;
  description: string;
  /** Tailwind color classes for the chip background / border / text */
  chipClass: string;
  /** Lucide icon name (consumer imports explicitly) */
  iconName: string;
}

export const IMPACT_EVENT_TYPE_META: Record<ImpactEventType, ImpactEventTypeMeta> = {
  revenue_missed: {
    label: 'Revenue Missed',
    description: 'Deals lost, upsells blocked, or ARR impact tied to this item not being done.',
    chipClass: 'bg-amber-50 border-amber-300 text-amber-800',
    iconName: 'TrendingDown',
  },
  incident: {
    label: 'Incident',
    description: 'An outage, degradation, or production failure that this item would have prevented.',
    chipClass: 'bg-red-50 border-red-300 text-red-800',
    iconName: 'AlertTriangle',
  },
  churn: {
    label: 'Churn',
    description: 'Customer(s) lost or downgraded because this item was not delivered.',
    chipClass: 'bg-orange-50 border-orange-300 text-orange-800',
    iconName: 'UserMinus',
  },
  contract_risk: {
    label: 'Contract Risk',
    description: 'SLA breach, renewal at risk, or contractual obligation missed.',
    chipClass: 'bg-yellow-50 border-yellow-300 text-yellow-800',
    iconName: 'FileWarning',
  },
  compliance_breach: {
    label: 'Compliance Breach',
    description: 'Regulatory or policy violation that occurred due to this item being deferred.',
    chipClass: 'bg-purple-50 border-purple-300 text-purple-800',
    iconName: 'ShieldAlert',
  },
  reputation: {
    label: 'Reputation',
    description: 'Negative press, social media incident, or stakeholder trust damage.',
    chipClass: 'bg-pink-50 border-pink-300 text-pink-800',
    iconName: 'Megaphone',
  },
  other: {
    label: 'Other',
    description: 'Any other documented business impact not covered by the above categories.',
    chipClass: 'bg-gray-50 border-gray-300 text-gray-700',
    iconName: 'CircleDot',
  },
};

export const IMPACT_EVENT_TYPE_OPTIONS = (Object.keys(IMPACT_EVENT_TYPE_META) as ImpactEventType[]).map(
  (key) => ({ value: key, label: IMPACT_EVENT_TYPE_META[key].label }),
);

// ── Domain model ──────────────────────────────────────────────────────────────

export interface ItemImpactEvent {
  id: string;
  workspaceId: string;
  itemId: string;
  eventType: ImpactEventType;
  /** 1 (minor) – 5 (critical) */
  severity?: number;
  /** Optional monetary/magnitude amount */
  impactAmount?: number;
  impactCurrency: string;
  description: string;
  occurredAt: Date;
  recordedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ── Form model ────────────────────────────────────────────────────────────────

export interface ImpactEventForm {
  eventType: ImpactEventType;
  severity?: number;
  impactAmount?: string; // string in form, parsed to number on submit
  impactCurrency: string;
  description: string;
  occurredAt: string; // ISO date string from <input type="date">
}

export const EMPTY_IMPACT_EVENT_FORM: ImpactEventForm = {
  eventType: 'revenue_missed',
  severity: undefined,
  impactAmount: '',
  impactCurrency: 'USD',
  description: '',
  occurredAt: '',
};

// ── Aggregates ────────────────────────────────────────────────────────────────

export interface ImpactSummary {
  count: number;
  /** Sum of impact_amount values where currency matches the workspace default, null if none entered */
  totalAmount: number | null;
  currency: string;
  highestSeverity: number | null;
}
